import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TreeNode as LegacyTreeNode } from 'tree-model';

import type { Key, TreeNode as UadTreeNode } from '@iconik-io/uad';

import { searchCollectionTree } from '../components/modals/add_to_collection_advanced/thunks';
import { getTreeNodePathKey } from '../components/tree/get_tree_node_path_key';
import { TreeBreadcrumbs } from '../components/tree/tree_breadcrumbs/tree_breadcrumbs';
import { treeReducer } from '../constants/reducer_ids';
import {
  getCollectionContentsForNode,
  getCollections,
} from '../ducks/collection_fetch/thunks';
import {
  clearTree,
  modifyBranchByNode,
  setTreeSearchLoading,
  setTreeSearchQuery,
} from '../ducks/tree/thunks';
import type { TreeReducerId, TreeReducerModel } from '../ducks/tree/types';
import { useAppDispatch, useAppSelector } from '../store/react_redux_typed';
import { isCollectionMappedToStorage } from '../utils/collections';
import { UUID_ZERO } from '../utils/tree_handlers';

import styles from './use_app_state_collection_tree.module.css';

export type IsCollectionDisabled = (collection: TreeReducerModel) => boolean;

function toUadTreeNode(
  node: LegacyTreeNode<TreeReducerModel>,
  isSearchActive: boolean,
  rootTitle: string,
  isCollectionDisabled?: IsCollectionDisabled,
): UadTreeNode {
  const children = node.children;
  const isRoot = node.model.id === UUID_ZERO;
  const isSearchMatch = isSearchActive && node.parent?.model.id === UUID_ZERO;

  return {
    id: getTreeNodePathKey(node),
    title: isRoot ? rootTitle : (node.model.title ?? node.model.name ?? ''),
    disabled: isRoot ? undefined : isCollectionDisabled?.(node.model),
    titleIcon: isCollectionMappedToStorage(node.model) ? 'isg' : 'collection',
    loading: node.model.loading,
    page: node.model.page,
    pages: node.model.pages,
    hasChildItems: isRoot ? children.length > 0 : true,
    children:
      children.length > 0
        ? children.map((child) =>
            toUadTreeNode(
              child,
              isSearchActive,
              rootTitle,
              isCollectionDisabled,
            ),
          )
        : undefined,
    subtitle: isSearchMatch && node.model.ancestor_collections && (
      <TreeBreadcrumbs
        ancestorCollectionIds={node.model.ancestor_collections}
        className={styles.breadcrumb}
      />
    ),
  };
}

function deriveExpandedKeys(
  flatTree: LegacyTreeNode<TreeReducerModel>[],
): Set<Key> {
  return new Set(
    flatTree
      .filter((node) => node.model.expanded)
      .map((node) => getTreeNodePathKey(node)),
  );
}

export function useAppStateCollectionTree({
  reducerId,
  rootFolderName,
  isCollectionDisabled,
}: {
  reducerId: TreeReducerId;
  rootFolderName?: string;
  isCollectionDisabled?: IsCollectionDisabled;
}) {
  const dispatch = useAppDispatch();
  const collectionsTree = useAppSelector(
    (state) => state[treeReducer[reducerId]],
  );

  const [searchText, setSearchText] = useState(
    collectionsTree.treeSearchQuery ?? '',
  );
  const [completedSearchText, setCompletedSearchText] = useState(
    collectionsTree.treeSearchQuery ?? '',
  );

  const isSearchActive = searchText.trim().length > 0;
  const isSearchPending =
    isSearchActive && searchText.trim() !== completedSearchText.trim();
  const isTreeLoading =
    Boolean(collectionsTree.treeModel.treeRoot.model.loading) &&
    collectionsTree.treeModel.treeRoot.children.length === 0;

  const treeNodes = useMemo(() => {
    const root = collectionsTree.treeModel?.treeRoot;
    if (
      !root ||
      isSearchPending ||
      isTreeLoading ||
      (isSearchActive && root.children.length === 0)
    ) {
      return [];
    }
    const rootTitle = rootFolderName ?? '/';
    return (isSearchActive ? root.children : [root]).map((node) =>
      toUadTreeNode(node, isSearchActive, rootTitle, isCollectionDisabled),
    );
  }, [
    collectionsTree.treeModel,
    isCollectionDisabled,
    isSearchActive,
    isSearchPending,
    isTreeLoading,
    rootFolderName,
  ]);

  const expandedKeys = useMemo(
    () => deriveExpandedKeys(collectionsTree.flatTree),
    [collectionsTree.flatTree],
  );

  useEffect(() => {
    if (!collectionsTree.flatTree.length) {
      dispatch(getCollections({}, reducerId));
    }
  }, [collectionsTree.flatTree.length, reducerId]);

  const handleSearch = useCallback(
    async (value: string) => {
      dispatch(setTreeSearchLoading(reducerId, true));
      dispatch(setTreeSearchQuery(reducerId, value));
      try {
        await dispatch(searchCollectionTree(value, reducerId));
      } finally {
        setCompletedSearchText(value);
        dispatch(setTreeSearchLoading(reducerId, false));
      }
    },
    [reducerId],
  );

  const onExpandedChange = useCallback(
    (newKeys: Set<Key>) => {
      const currentKeys = deriveExpandedKeys(collectionsTree.flatTree);

      for (const key of newKeys) {
        if (!currentKeys.has(key)) {
          const node = collectionsTree.flatTree.find(
            (item) => getTreeNodePathKey(item) === key,
          );
          if (node) {
            const nodeChildren = node.children;
            if (!nodeChildren.length && !node.model.loading) {
              dispatch(getCollectionContentsForNode(node, {}, reducerId));
            }
            dispatch(modifyBranchByNode(reducerId, node, { expanded: true }));
          }
        }
      }

      for (const key of currentKeys) {
        if (!newKeys.has(key)) {
          const node = collectionsTree.flatTree.find(
            (item) => getTreeNodePathKey(item) === key,
          );
          if (node) {
            dispatch(modifyBranchByNode(reducerId, node, { expanded: false }));
            if (String(key) === UUID_ZERO) {
              dispatch(clearTree(reducerId));
              dispatch(getCollections({}, reducerId));
            }
          }
        }
      }
    },
    [collectionsTree.flatTree, reducerId],
  );

  const onLoadMore = useCallback(
    (parentId: string) => {
      const node = collectionsTree.flatTree.find(
        (item) => getTreeNodePathKey(item) === parentId,
      );
      if (!node) {
        return;
      }

      const page = Number(node.model.page) + 1;
      if (parentId !== UUID_ZERO) {
        dispatch(getCollectionContentsForNode(node, { page }, reducerId));
      } else if (searchText.trim()) {
        dispatch(searchCollectionTree(searchText, reducerId, page));
      } else {
        dispatch(getCollections({ page }, reducerId));
      }
    },
    [collectionsTree.flatTree, searchText, reducerId],
  );

  const getCollection = useCallback(
    (key: Key): TreeReducerModel | undefined =>
      collectionsTree.flatTree.find((item) => getTreeNodePathKey(item) === key)
        ?.model,
    [collectionsTree.flatTree],
  );

  return {
    treeNodes,
    expandedKeys,
    getCollection,
    onExpandedChange,
    onLoadMore,
    isLoading: isSearchActive
      ? isSearchPending || Boolean(collectionsTree.treeSearchLoading)
      : Boolean(collectionsTree.treeModel.treeRoot.model.loading),
    searchText,
    setSearchText,
    handleSearch,
  };
}
